import type {
  DocumentType,
  DocumentTypeDetail,
  DocumentTypeRef,
  DocumentTypeVariable,
  DocumentTypeVariableCategory
} from '@/document-types/domain/types/DocumentType';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const readBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const readStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((v) => typeof v === 'string') ? (value as string[]) : undefined;

const allowedVariableTypes = new Set(['string', 'number', 'date', 'boolean']);

const readVariableType = (value: unknown): DocumentTypeVariable['type'] | undefined => {
  if (typeof value !== 'string') return undefined;
  if (!allowedVariableTypes.has(value)) return undefined;
  return value as DocumentTypeVariable['type'];
};

const mapVariable = (dto: unknown): DocumentTypeVariable => {
  if (!isRecord(dto)) throw new Error('Invalid DocumentTypeVariable: expected an object');

  const path = readString(dto.path);
  const label = readString(dto.label);
  const type = readVariableType(dto.type);
  const example = typeof dto.example === 'string' ? dto.example : undefined;

  if (!path || !label || !type) throw new Error('Invalid DocumentTypeVariable: missing required fields');

  return { path, label, type, ...(example ? { example } : {}) };
};

const mapVariableCategory = (dto: unknown): DocumentTypeVariableCategory => {
  if (!isRecord(dto)) throw new Error('Invalid DocumentTypeVariableCategory: expected an object');

  const key = readString(dto.key);
  const label = readString(dto.label);
  const variables = Array.isArray(dto.variables) ? dto.variables.map(mapVariable) : undefined;

  if (!key || !label || !variables) throw new Error('Invalid DocumentTypeVariableCategory: missing required fields');

  return { key, label, variables };
};

const mapRef = (dto: unknown): DocumentTypeRef => {
  if (!isRecord(dto)) throw new Error('Invalid DocumentType: expected an object');

  const applicationKey = readString(dto.applicationKey);
  const moduleKey = readString(dto.moduleKey);
  const documentTypeKey = readString(dto.documentTypeKey);

  if (!applicationKey || !moduleKey || !documentTypeKey) {
    throw new Error('Invalid DocumentType: missing required key fields');
  }

  return { applicationKey, moduleKey, documentTypeKey };
};

export const toDocumentType = (dto: unknown): DocumentType => {
  if (!isRecord(dto)) throw new Error('Invalid DocumentType: expected an object');

  const ref = mapRef(dto);
  const displayName = readString(dto.displayName);

  // The pdf-templates BE doesn't expose `isGlobal` directly — it uses
  // `isSystem` (built-in document types vs tenant-defined). Accept either,
  // and default to `false` when neither is present so the editor's New
  // Template dialog can still enumerate types from the BE without crashing.
  const isGlobal = readBoolean(dto.isGlobal) ?? readBoolean(dto.isSystem) ?? false;

  if (!displayName) throw new Error('Invalid DocumentType: missing required fields');

  return { ...ref, displayName, isGlobal };
};

export const toDocumentTypeDetail = (dto: unknown): DocumentTypeDetail => {
  if (!isRecord(dto)) throw new Error('Invalid DocumentTypeDetail: expected an object');

  const base = toDocumentType(dto);

  const variableCategoriesRaw = dto.variableCategories;
  const variableCategories = Array.isArray(variableCategoriesRaw) ? variableCategoriesRaw.map(mapVariableCategory) : [];

  const lineItemColumns = readStringArray(dto.lineItemColumns) ?? [];

  const scopeFieldAttributesRaw = dto.scopeFieldAttributes;
  const scopeFieldAttributes = Array.isArray(scopeFieldAttributesRaw)
    ? scopeFieldAttributesRaw.map((v) => {
        if (!isRecord(v)) throw new Error('Invalid scopeFieldAttributes item');
        const key = readString(v.key);
        const label = readString(v.label);
        if (!key || !label) throw new Error('Invalid scopeFieldAttributes item: missing key/label');
        return { key, label };
      })
    : undefined;

  if (!scopeFieldAttributes) {
    return {
      ...base,
      variableCategories,
      lineItemColumns,
      scopeFieldAttributes: []
    };
  }

  return {
    ...base,
    variableCategories,
    lineItemColumns,
    scopeFieldAttributes
  };
};
